// convex/lib/config/domains/security.ts

import { validatorUsers } from "../../../system/users";
import { validatorUserInvitation } from "../../../system/users/user_invitations";
import { validatorRole } from "../../../system/roles";
import { validatorPermissions } from "../../../system/permissions";
import { validatorCompany } from "../../../companies";
import { permissionsRoleRule } from "../../../system/permissions/filter_rules";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// SECURITY DOMAIN - Status Values for i18n
// ============================================================================

export const ROLE_STATUSES = ["active", "inactive"] as const;
export const USER_STATUSES = [
  "active",
  "inactive",
  "pending",
  "suspended",
] as const;

export const securityDomain: DomainGroup = {
  id: "security",
  name: "Seguridad y Accesos",
  description: "Gestión de usuarios, roles y permisos",
  icon: "Shield",
  order: 101,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "security",
    keys: {
      title: "security.title",
      description: "security.description",
    },
  },

  modules: [
    {
      id: "users",
      name: "Usuarios",
      description: "Gestión de usuarios y permisos",
      icon: "Users",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/users", "/super/users"],
      navigation: {
        showInNav: true,
        contexts: ["settings", "super"],
        navOrder: 3,
        status: "active",
      },

      i18n: {
        namespace: "security.users",
        keys: {
          title: "security.users.title",
          description: "security.users.description",
          longDescription: "security.users.longDescription",
          searchPlaceholder: "security.users.searchPlaceholder",
          emptyState: "security.users.emptyState",
          actions: {
            create: "security.users.actions.create",
            edit: "security.users.actions.edit",
            delete: "security.users.actions.delete",
            view: "security.users.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [{ field: "status", values: USER_STATUSES }],
      },

      tables: [
        {
          tableName: "users",
          schema: validatorUsers,
          scope: "company" as TableScope,
          relations: [
            {
              key: "company",
              sourceField: "internalControl.companyId",
              schema: validatorCompany,
              tableName: "companies",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "role",
              sourceField: "roleId",
              schema: validatorRole,
              tableName: "roles",
            },
          ],
          searchFields: ["name", "email", "phone"],
          hasStatusIndex: true,
        },
        {
          tableName: "user_invitations",
          schema: validatorUserInvitation,
          scope: "company" as TableScope,
          relations: [
            { key: "invitedBy", schema: validatorUsers, tableName: "users" },
            {
              key: "company",
              schema: validatorCompany,
              tableName: "companies",
            },
          ],
          searchFields: ["userId"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [
        { tableName: "roles" },
        { tableName: "branches" },
        { tableName: "companies" },
        { tableName: "authAccounts" },
      ],
    },
    {
      id: "authAccounts",
      name: "Cuentas de Autenticación",
      description: "Gestión de cuentas de autenticación",
      icon: "KeyRound",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/security/auth-accounts"],
      navigation: {
        showInNav: false,
        contexts: ["none"],
        navOrder: 999,
        status: "active",
      },

      i18n: {
        namespace: "security.authAccounts",
        keys: {
          title: "security.authAccounts.title",
          description: "security.authAccounts.description",
          longDescription: "security.authAccounts.longDescription",
          searchPlaceholder: "security.authAccounts.searchPlaceholder",
          emptyState: "security.authAccounts.emptyState",
          actions: {
            create: "security.authAccounts.actions.create",
            edit: "security.authAccounts.actions.edit",
            delete: "security.authAccounts.actions.delete",
            view: "security.authAccounts.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          // authAccounts no tenia config en tableConfigs — schema minimo
          tableName: "authAccounts",
          schema: {},
          scope: "company" as TableScope,
        },
      ],
      readOnlyTables: [{ tableName: "users" }],
    },
    {
      id: "roles",
      name: "Roles",
      description: "Gestión de roles y permisos",
      icon: "ShieldCheck",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/roles", "/super/roles"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 2,
        status: "active",
      },

      i18n: {
        namespace: "security.roles",
        keys: {
          title: "security.roles.title",
          description: "security.roles.description",
          longDescription: "security.roles.longDescription",
          searchPlaceholder: "security.roles.searchPlaceholder",
          emptyState: "security.roles.emptyState",
          actions: {
            create: "security.roles.actions.create",
            edit: "security.roles.actions.edit",
            delete: "security.roles.actions.delete",
            view: "security.roles.actions.view",
          },
        },
        translatableFields: ["name", "description"],
        translatableEnums: [{ field: "status", values: ROLE_STATUSES }],
      },

      tables: [
        {
          tableName: "roles",
          schema: validatorRole,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "description"],
          hasStatusIndex: false,
        },
      ],
      readOnlyTables: [{ tableName: "companies" }],
    },
    {
      id: "permissions",
      name: "Permisos",
      description: "Configuración de permisos",
      icon: "Lock",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/permissions", "/super/permissions"],
      navigation: {
        showInNav: false,
        contexts: ["super"],
        navOrder: 8,
        status: "active",
      },

      i18n: {
        namespace: "security.permissions",
        keys: {
          title: "security.permissions.title",
          description: "security.permissions.description",
          longDescription: "security.permissions.longDescription",
          searchPlaceholder: "security.permissions.searchPlaceholder",
          emptyState: "security.permissions.emptyState",
          actions: {
            create: "security.permissions.actions.create",
            edit: "security.permissions.actions.edit",
            delete: "security.permissions.actions.delete",
            view: "security.permissions.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "permissions",
          schema: validatorPermissions,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "role",
              sourceField: "roleId",
              schema: validatorRole,
              tableName: "roles",
            },
          ],
          searchFields: ["module"],
          hasStatusIndex: false,
          customRules: [permissionsRoleRule],
        },
      ],
      readOnlyTables: [{ tableName: "roles" }],
    },
  ],
};
